import * as React from 'react';
import {
  Check,
  Edit3,
  Facebook,
  Hash,
  ImagePlus,
  Instagram,
  Info,
  MoreVertical,
  Plus,
  RefreshCw,
  Share2,
  Sparkles,
  Trash2,
  UploadCloud,
  X,
  Youtube,
} from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDebounce } from 'core/hooks/useDebounce';
import { showGlobalModal } from 'core/components/GlobalModal';
import { PaginatedCardGrid } from 'core/components/PaginatedCardGrid';
import { SetupLoader } from 'core/components/SetupComponents/SetupLoader';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import httpclient from 'libs/Axios';
import { dmTextGenerationApi } from 'features/DigitalMarketing/api/dmTextGenerationApi';
import { useImageEnhancer } from 'features/DigitalMarketing/hooks/useImageEnhancer';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingSearchInput } from 'features/DigitalMarketing/components/DigitalMarketingSearchInput';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import { Modal } from 'features/DigitalMarketing/components/ui/Modal';
import { Toggle } from 'features/DigitalMarketing/components/ui/Toggle';
import {
  maxDescriptionGenerations,
} from 'features/DigitalMarketing/constants/formStyles';
import {
  buildCreatePostPath,
  CREATE_POST_BASE_PATH,
} from 'features/DigitalMarketing/routes/createPostPaths';
import {
  extractUploadedUrl,
  getApiErrorMessage,
  getResponseList,
} from 'features/DigitalMarketing/utils/apiResponse';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMInput,
  DMLabel,
  DMText,
  DMTextarea,
} from '../../components/Primitives';
import './create-post.css';

type PostImage = {
  url: string;
  name: string;
};

type PostFormValues = {
  postId: number;
  title: string;
  description: string;
  hashtags: string;
  images: PostImage[];
};

type PostPlatformKey = 'facebook' | 'instagram' | 'youtube';
type PreviewPlatformKey = 'facebook' | 'instagram' | 'whatsapp' | 'gmb';
type DetailPlatformKey =
  | PostPlatformKey
  | 'whatsapp'
  | 'gmb'
  | 'parentApp'
  | 'facultyApp';

const postPlatformLabels: Record<PostPlatformKey, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
};

type PostItem = {
  id: number;
  title: string;
  description: string;
  images: string[];
  instagram: boolean;
  facebook: boolean;
  youtube: boolean;
  availablePlatforms: PostPlatformKey[];
  createdAt: string;
  updatedAt: string;
  date: string;
  time: string;
  school: string;
  raw: any;
};

type SocialPostMediaType = 'PHOTO' | 'VIDEO' | 'REEL';

type SocialPostPayload = {
  MEDIA_TYPE: SocialPostMediaType;
  TITLE: string;
  DESCRIPTION?: string;
  MEDIA: string[];
  BRANCH_ID?: number | string;
};

const emptyPostForm: PostFormValues = {
  postId: 0,
  title: '',
  description: '',
  hashtags: '',
  images: [],
};

const referencePlatformPaths = {
  facebook:
    'M13.5 22v-8h2.8l.4-3.2h-3.2V8.8c0-.9.3-1.5 1.6-1.5h1.7V4.4c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.2H7.3V14h2.8v8z',
  instagram:
    'M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.3 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .3-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.3-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.3 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 3.3A6.5 6.5 0 1 0 18.5 12 6.5 6.5 0 0 0 12 5.5zm0 10.7A4.2 4.2 0 1 1 16.2 12 4.2 4.2 0 0 1 12 16.2zM18.8 4.2a1.5 1.5 0 1 0 1.5 1.5 1.5 1.5 0 0 0-1.5-1.5z',
  youtube:
    'M21.6 7.2a2.5 2.5 0 0 0-1.8-1.8C18.2 5 12 5 12 5s-6.2 0-7.8.4A2.5 2.5 0 0 0 2.4 7.2C2 8.8 2 12 2 12s0 3.2.4 4.8a2.5 2.5 0 0 0 1.8 1.8C5.8 19 12 19 12 19s6.2 0 7.8-.4a2.5 2.5 0 0 0 1.8-1.8C22 15.2 22 12 22 12s0-3.2-.4-4.8zM10 15.2V8.8l5.4 3.2z',
  whatsapp:
    'M12 2C6.5 2 2 6.5 2 12c0 1.9.5 3.6 1.4 5.1L2 22l5.1-1.3A9.9 9.9 0 0 0 12 22c5.5 0 10-4.5 10-10S17.5 2 12 2zm5.6 14.2c-.2.7-1.4 1.3-1.9 1.3-.5.1-1-.2-3.5-.7-3-1.2-4.9-4.2-5-4.4-.2-.2-1.2-1.6-1.2-3.1s.8-2.2 1-2.5c.3-.3.6-.4.8-.4h.6c.2 0 .4-.1.7.5.2.6.8 2.1.9 2.2.1.2.1.3 0 .5-.1.2-.2.3-.3.5-.2.2-.3.4-.5.5-.1.2-.3.3-.1.6.2.3.8 1.3 1.7 2.1 1.1 1 2.1 1.3 2.4 1.5.3.1.5.1.6-.1.2-.2.8-.9.9-1.2.2-.3.4-.2.7-.1.3.1 1.7.8 2 1 .3.1.5.2.6.3.1.2.1.8-.2 1.5z',
  gmb: 'M12 2 3 7v10l9 5 9-5V7zM7.5 9.5h9v1.8h-9zm0 3.5h6.4v1.8H7.5z',
};

const previewPlatformMeta: Record<PreviewPlatformKey, {
  name: string;
  accent: string;
  audience: string;
  handle: string;
  ratio: string;
  aspect: string;
  path: string;
  actions: Array<{ label: string; path: string }>;
}> = {
  facebook: { name: 'Facebook', accent: '#1877F2', audience: 'Public', handle: 'Soomuch Demo School', ratio: '1.91:1', aspect: '1.91 / 1', path: referencePlatformPaths.facebook, actions: [
    { label: 'Like', path: 'M7 10v11H4V10zM7 10l4-7a2.2 2.2 0 0 1 3 2v4h4.5a2 2 0 0 1 2 2.4l-1.4 6A2 2 0 0 1 17 21H7' },
    { label: 'Comment', path: 'M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z' },
    { label: 'Share', path: 'M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M12 16V3m-4 4 4-4 4 4' },
  ] },
  instagram: { name: 'Instagram', accent: '#C13584', audience: 'Followers', handle: 'soomuchdemoschool', ratio: '1:1', aspect: '1 / 1', path: referencePlatformPaths.instagram, actions: [
    { label: 'Like', path: 'M12 20s-7-4.3-7-9a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 4.7-7 9-7 9z' },
    { label: 'Comment', path: 'M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z' },
    { label: 'Send', path: 'M21 3 3 10.5l7 2.6L12.6 21z' },
  ] },
  whatsapp: { name: 'WhatsApp', accent: '#25D366', audience: 'Parent groups', handle: 'School Broadcast', ratio: '1:1', aspect: '1 / 1', path: referencePlatformPaths.whatsapp, actions: [
    { label: 'Reply', path: 'M9 17H5a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-4l-3 4z' },
    { label: 'Forward', path: 'M4 12h13m-4-5 5 5-5 5' },
    { label: 'Read 0', path: 'm2 12.5 4 4L14 8m4 0-6.5 8.5' },
  ] },
  gmb: { name: 'Google Business', accent: '#0F9D58', audience: 'Local search', handle: 'Soomuch Demo School', ratio: '4:3', aspect: '4 / 3', path: referencePlatformPaths.gmb, actions: [
    { label: 'Directions', path: 'M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11z' },
    { label: 'Call', path: 'M4 5a2 2 0 0 1 2-2h1.6l1.6 4-2 1.3a11 11 0 0 0 4.5 4.5l1.3-2 4 1.6V19a2 2 0 0 1-2 2A15 15 0 0 1 4 5z' },
    { label: 'Review', path: 'm12 3.5 2.6 5.4 5.9.8-4.3 4.2 1 5.9-5.2-2.8-5.2 2.8 1-5.9L3.5 9.7l5.9-.8z' },
  ] },
};

const detailPlatformMeta: Array<{
  key: DetailPlatformKey;
  name: string;
  code: string;
  chip: string;
  sub: string;
  backendKey?: PostPlatformKey;
}> = [
  { key: 'instagram', name: 'Instagram', code: 'IG', chip: 'instagram', sub: '@soomuchschool · 6.1K followers', backendKey: 'instagram' },
  { key: 'facebook', name: 'Facebook', code: 'FB', chip: 'facebook', sub: 'Soomuch Demo School page · 8.4K followers', backendKey: 'facebook' },
  { key: 'youtube', name: 'YouTube', code: 'YT', chip: 'youtube', sub: 'Soomuch Demo School channel', backendKey: 'youtube' },
  { key: 'whatsapp', name: 'WhatsApp', code: 'WA', chip: 'whatsapp', sub: 'Broadcast to parent groups' },
  { key: 'gmb', name: 'Google My Business', code: 'G', chip: 'gmb', sub: 'Posts to your Business Profile' },
  { key: 'parentApp', name: 'Parent App', code: 'PA', chip: 'parent-app', sub: 'Notifies all parents' },
  { key: 'facultyApp', name: 'Faculty App', code: 'FA', chip: 'faculty-app', sub: 'Shares with staff & faculty' },
];

const ReferencePathIcon = ({ path }: { path: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d={path} /></svg>
);

const ReferenceStrokeIcon = ({ path }: { path: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={path} />
  </svg>
);
const allowedImageTypes = ['image/png', 'image/jpg', 'image/jpeg'];
const maxImageSize = 15 * 1024 * 1024;
const noImagesPlaceholder =
  'https://placehold.co/600x400/E1DCFD/5E4DE0?text=No+Images';
const emptyPlatforms = {
  facebook: false,
  instagram: false,
  youtube: false,
  website: false,
  whatsapp: false,
};

const splitPostCopy = (copy = '') => {
  const hashtagLines = copy
    .split(/\r?\n/)
    .filter(line => line.trim().startsWith('#'));
  const description = copy
    .split(/\r?\n/)
    .filter(line => !line.trim().startsWith('#'))
    .join('\n')
    .trim();

  return { description, hashtags: hashtagLines.join(' ').trim() };
};

const combinePostCopy = (description: string, hashtags: string) =>
  [description.trim(), hashtags.trim()].filter(Boolean).join('\n\n');

const normalizeImages = (item: any): string[] => {
  const imageValue =
    item?.image_urls ||
    item?.IMAGE_URLS ||
    item?.images ||
    item?.IMAGES ||
    item?.image_url ||
    item?.IMAGE_URL ||
    '';

  if (Array.isArray(imageValue)) {
    return imageValue.filter(
      (image): image is string => typeof image === 'string' && Boolean(image)
    );
  }

  if (typeof imageValue === 'string' && imageValue.trim()) {
    try {
      const parsed = JSON.parse(imageValue);
      if (Array.isArray(parsed)) {
        return parsed.filter(
          (image): image is string =>
            typeof image === 'string' && Boolean(image)
        );
      }
    } catch (_error) {
      return imageValue
        .split(',')
        .map(image => image.trim())
        .filter(Boolean);
    }

    return [imageValue];
  }

  return [];
};

const formatPostDateTime = (dateValue: string) => {
  if (!dateValue) {
    return { date: '-', time: '-' };
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return { date: dateValue, time: '-' };
  }

  return {
    date: parsed.toLocaleDateString('en-GB'),
    time: parsed.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
};

const hasResponseKey = (item: any, key: string) =>
  Object.prototype.hasOwnProperty.call(item || {}, key);

const normalizePlatformName = (value: string) =>
  value.trim().toLowerCase().replace(/[\[\]"']/g, '');

const normalizePlatformFlag = (value: unknown) =>
  value === true || value === 1 || value === '1' ||
  (typeof value === 'string' && ['true', 'yes', 'published', 'live'].includes(value.trim().toLowerCase()));

const normalizePlatformArray = (platform: unknown): string[] => {
  if (Array.isArray(platform)) {
    return platform
      .filter((item): item is string => typeof item === 'string')
      .map(normalizePlatformName);
  }
  if (typeof platform !== 'string' || !platform.trim()) {
    if (platform && typeof platform === 'object') {
      return Object.entries(platform)
        .filter(([, value]) => normalizePlatformFlag(value))
        .map(([key]) => normalizePlatformName(key));
    }
    return [];
  }
  try {
    const parsed = JSON.parse(platform);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((item): item is string => typeof item === 'string')
        .map(normalizePlatformName);
    }
  } catch (_error) {
    return platform
      .split(',')
      .map(normalizePlatformName)
      .filter(Boolean);
  }
  return [];
};

const mapPostRow = (item: any): PostItem => {
  const createdAt =
    item?.CREATED_AT || item?.created_at || item?.createdAt || '';
  const updatedAt =
    item?.UPDATED_AT || item?.updated_at || item?.updatedAt || '';
  const displayDateTime = updatedAt || createdAt;
  const dateTime = formatPostDateTime(displayDateTime);
  const platformState =
    item?.platform_status || item?.PLATFORM_STATUS ||
    item?.social_platforms || item?.SOCIAL_PLATFORMS || {};
  const legacyPlatforms = normalizePlatformArray(
    item?.platform || item?.PLATFORM || item?.platforms || item?.PLATFORMS ||
    item?.published_platforms || item?.PUBLISHED_PLATFORMS || platformState
  );
  const instagram = normalizePlatformFlag(
    item?.instagram ?? item?.INSTAGRAM ?? platformState?.instagram ?? platformState?.INSTAGRAM
  ) ||
    legacyPlatforms.some(platform => platform === 'instagram' || platform === 'ig');
  const facebook = normalizePlatformFlag(
    item?.facebook ?? item?.FACEBOOK ?? platformState?.facebook ?? platformState?.FACEBOOK
  ) ||
    legacyPlatforms.some(platform => platform === 'facebook' || platform === 'fb');
  const youtube = normalizePlatformFlag(
    item?.youtube ?? item?.YOUTUBE ?? platformState?.youtube ?? platformState?.YOUTUBE
  ) ||
    legacyPlatforms.some(platform => platform === 'youtube' || platform === 'yt');
  const hasBooleanPlatformKeys = ['facebook', 'instagram', 'youtube'].some(
    platform => hasResponseKey(item, platform) || hasResponseKey(item, platform.toUpperCase())
  );

  return {
    id: Number(item?.post_id || item?.POST_ID || item?.id || item?.ID || 0),
    title: item?.title || item?.TITLE || 'Untitled Post',
    description: item?.description || item?.DESCRIPTION || '',
    images: normalizeImages(item),
    instagram,
    facebook,
    youtube,
    availablePlatforms: hasBooleanPlatformKeys ? [
      ...(hasResponseKey(item, 'facebook') || hasResponseKey(item, 'FACEBOOK') ? ['facebook' as const] : []),
      ...(hasResponseKey(item, 'instagram') || hasResponseKey(item, 'INSTAGRAM') ? ['instagram' as const] : []),
      ...(hasResponseKey(item, 'youtube') || hasResponseKey(item, 'YOUTUBE') ? ['youtube' as const] : []),
    ] : ['facebook', 'instagram', 'youtube'],
    createdAt,
    updatedAt,
    date: dateTime.date,
    time: dateTime.time,
    school: 'DemoSchool',
    raw: item,
  };
};

const isPostLive = (post: PostItem) =>
  post.facebook || post.instagram || post.youtube;

let cachedPostRows: PostItem[] = [];
let cachedPostTotal = 0;

const getPostList = (response: any) =>
  response?.data?.data?.Posts ||
  response?.data?.data?.POSTS ||
  response?.data?.data?.posts ||
  getResponseList(response);

const getPostTotal = (response: any) =>
  Number(
    response?.data?.data?.Total ||
      response?.data?.data?.TOTAL ||
      response?.data?.data?.total ||
      getPostList(response).length
  );

const getPostDetails = (response: any) => {
  const data = response?.data?.data || {};
  return data?.Post || data?.POST || data?.post || data;
};

const openSocialPostUrl = (response: any) => {
  const url = response?.data?.data?.url;

  if (typeof url === 'string' && url.trim()) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const buildFacebookPostPayload = (post: PostItem): SocialPostPayload => {
  const raw = post.raw || {};
  const title = raw?.title || raw?.TITLE || post.title;
  const description = raw?.description || raw?.DESCRIPTION || post.description;
  const media = normalizeImages(raw);

  return {
    MEDIA_TYPE: 'PHOTO',
    TITLE: title,
    DESCRIPTION: description,
    MEDIA: media.length ? media : post.images,
  };
};

const PostCard = ({
  item,
  onPreview,
  onEdit,
  onDelete,
  onPublish,
}: {
  item: PostItem;
  onPreview: (post: PostItem) => void;
  onEdit: (post: PostItem) => void;
  onDelete: (post: PostItem) => void;
  onPublish: (post: PostItem) => void;
}) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const imageUrl = item.images[0] || noImagesPlaceholder;
  const hasPublishedPlatform = item.facebook || item.instagram || item.youtube;
  const editLockedMessage =
    'This post is already published. Published posts cannot be edited.';
  const deleteLockedMessage =
    'This post is already published. Published posts cannot be deleted.';
  const publishedPlatformCodes = [
    ...(item.facebook ? ['FB'] : []),
    ...(item.instagram ? ['IG'] : []),
    ...(item.youtube ? ['YT'] : []),
  ];

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  return (
    <DMDiv
      as="article"
      className="cp-post-card group relative min-w-0 rounded-2xl border border-gray-200 bg-white transition-all hover:shadow-lg"
    >
      <DMButton
        type="button"
        onClick={() => onPreview(item)}
        className="cp-post-image relative block aspect-[1.72] w-full overflow-hidden rounded-t-2xl bg-gray-200 text-left"
      >
        <DMImage
          src={imageUrl}
          alt={item.title}
          onError={event => {
            event.currentTarget.src = noImagesPlaceholder;
          }}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <DMText
          as="span"
          className={`cp-post-status-chip ${
            hasPublishedPlatform ? 'live' : 'ready'
          }`}
        >
          {hasPublishedPlatform
            ? `LIVE · ${publishedPlatformCodes.join(' ')}`
            : 'READY TO PUBLISH'}
        </DMText>
      </DMButton>

      <DMDiv className="cp-post-copy flex items-start justify-between gap-[1vw] p-5">
        <DMDiv
          className="min-w-0 flex-1 cursor-pointer"
          onClick={() => onPreview(item)}
        >
          <DMText
            as="h2"
            className="truncate text-[2.25vh] font-bold leading-tight text-gray-950"
          >
            {item.title}
          </DMText>
          <DMText className="mt-[1.2vh] truncate text-[1.55vh] font-medium leading-tight text-gray-500">
            {item.date} | {item.time} | {item.school}
          </DMText>
        </DMDiv>

        {/* Card actions are intentionally hidden: the reference list opens details from the card itself. */}
        <DMDiv className="hidden" ref={menuRef}>
          <DMButton
            type="button"
            onClick={event => {
              event.stopPropagation();
              setShowMenu(current => !current);
            }}
            className="flex h-[3.2vh] w-[2vw] items-center justify-center text-gray-950"
            aria-label={`Open actions for ${item.title}`}
          >
            <MoreVertical className="h-[2.8vh] w-[2.8vh]" strokeWidth={2.5} />
          </DMButton>

          {showMenu && (
            <DMDiv className="absolute bottom-full right-0 z-10 mb-1 w-44 overflow-hidden rounded-lg border border-gray-100 bg-white shadow-lg">
              <DMButton
                type="button"
                onClick={() => {
                  setShowMenu(false);
                  onPublish(item);
                }}
                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <Share2 size={14} /> Publish
              </DMButton>
              <DMButton
                type="button"
                onClick={() => {
                  setShowMenu(false);
                  if (!hasPublishedPlatform) {
                    onEdit(item);
                  }
                }}
                aria-disabled={hasPublishedPlatform}
                title={hasPublishedPlatform ? editLockedMessage : 'Edit'}
                className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${
                  hasPublishedPlatform
                    ? 'cursor-not-allowed text-gray-400'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Edit3 size={14} /> Edit
              </DMButton>
              <DMButton
                type="button"
                onClick={() => {
                  setShowMenu(false);
                  if (!hasPublishedPlatform) {
                    onDelete(item);
                  }
                }}
                aria-disabled={hasPublishedPlatform}
                title={hasPublishedPlatform ? deleteLockedMessage : 'Delete'}
                className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${
                  hasPublishedPlatform
                    ? 'cursor-not-allowed text-gray-400'
                    : 'text-red-600 hover:bg-red-50'
                }`}
              >
                <Trash2 size={14} /> Delete
              </DMButton>
            </DMDiv>
          )}
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};

const PostCardSkeleton = () => (
  <DMDiv className="min-w-0 animate-pulse" aria-hidden="true">
    <DMDiv className="aspect-[1.72] rounded-[8px] bg-gray-200" />
    <DMDiv className="mt-[2vh] flex items-start justify-between gap-[1vw]">
      <DMDiv className="min-w-0 flex-1">
        <DMDiv className="h-[2.4vh] w-[75%] rounded bg-gray-200" />
        <DMDiv className="mt-[1.2vh] h-[1.6vh] w-[58%] rounded bg-gray-200" />
      </DMDiv>
    </DMDiv>
  </DMDiv>
);

const DisabledPostToggle = ({ checked }: { checked: boolean }) => (
  <DMButton
    type="button"
    disabled
    className={`w-[4.4vh] h-[2.4vh] rounded-[2vh] p-[0.3vh] transition-colors duration-200 ease-in-out cursor-not-allowed opacity-60 ${
      checked ? 'bg-primary-main' : 'bg-gray-200'
    }`}
  >
    <DMDiv
      className={`w-[1.8vh] h-[1.8vh] bg-white rounded-full shadow-sm transform transition-transform duration-200 ease-in-out ${
        checked ? 'translate-x-[2vh]' : 'translate-x-0'
      }`}
    />
  </DMButton>
);

const PostPublishModal = ({
  isOpen,
  onClose,
  platforms,
  visiblePlatforms,
  requestPublish,
  isPublishing,
  publishingPlatform,
}: {
  isOpen: boolean;
  onClose: () => void;
  platforms: typeof emptyPlatforms;
  visiblePlatforms: PostPlatformKey[];
  requestPublish: (key: PostPlatformKey) => void;
  isPublishing: boolean;
  publishingPlatform: PostPlatformKey | null;
}) => (
  <Modal isOpen={isOpen} onClose={isPublishing ? () => undefined : onClose}>
    <DMDiv className="text-left">
      <DMText as="h2" className="mb-2 text-2xl font-bold text-gray-900">
        Publish Post
      </DMText>
      <DMText className="mb-8 text-gray-500">
        Select the social media platforms where you want to publish your post
      </DMText>

      <DMDiv className="space-y-4">
        {visiblePlatforms.includes('facebook') && (
          <DMDiv className="flex items-center justify-between border-b border-gray-100 pb-4">
            <DMDiv className="flex items-center gap-3">
              <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-[#1877F2] text-white">
                <Facebook size={18} />
              </DMDiv>
              <DMText as="span" className="font-medium text-gray-900">
                Share on Facebook
              </DMText>
            </DMDiv>
            {platforms.facebook || isPublishing ? (
              <DMDiv className="flex items-center gap-3">
                {publishingPlatform === 'facebook' && (
                  <DMText
                    as="span"
                    className="text-sm font-medium text-primary-main"
                  >
                    Posting...
                  </DMText>
                )}
                <DisabledPostToggle checked={platforms.facebook} />
              </DMDiv>
            ) : (
              <Toggle
                checked={platforms.facebook}
                onChange={() => requestPublish('facebook')}
              />
            )}
          </DMDiv>
        )}

        {visiblePlatforms.includes('instagram') && (
          <DMDiv className="flex items-center justify-between border-b border-gray-100 pb-4">
            <DMDiv className="flex items-center gap-3">
              <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 text-white">
                <Instagram size={18} />
              </DMDiv>
              <DMText as="span" className="font-medium text-gray-900">
                Share on Instagram
              </DMText>
            </DMDiv>
            {platforms.instagram || isPublishing ? (
              <DMDiv className="flex items-center gap-3">
                {publishingPlatform === 'instagram' && (
                  <DMText
                    as="span"
                    className="text-sm font-medium text-primary-main"
                  >
                    Posting...
                  </DMText>
                )}
                <DisabledPostToggle checked={platforms.instagram} />
              </DMDiv>
            ) : (
              <Toggle
                checked={platforms.instagram}
                onChange={() => requestPublish('instagram')}
              />
            )}
          </DMDiv>
        )}

        {visiblePlatforms.includes('youtube') && (
          <DMDiv className="flex items-center justify-between border-b border-gray-100 pb-4">
            <DMDiv className="flex items-center gap-3">
              <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-[#FF0000] text-white">
                <Youtube size={18} />
              </DMDiv>
              <DMText as="span" className="font-medium text-gray-900">
                Share on YouTube
              </DMText>
            </DMDiv>
            {platforms.youtube || isPublishing ? (
              <DMDiv className="flex items-center gap-3">
                {publishingPlatform === 'youtube' && (
                  <DMText
                    as="span"
                    className="text-sm font-medium text-primary-main"
                  >
                    Posting...
                  </DMText>
                )}
                <DisabledPostToggle checked={platforms.youtube} />
              </DMDiv>
            ) : (
              <Toggle
                checked={platforms.youtube}
                onChange={() => requestPublish('youtube')}
              />
            )}
          </DMDiv>
        )}
      </DMDiv>

      {isPublishing && (
        <DMDiv className="mt-6 flex items-center gap-3 rounded-lg bg-primary-main/10 px-4 py-3 text-sm font-medium text-primary-main">
          <DMText
            as="span"
            className="h-4 w-4 animate-spin rounded-full border-2 border-primary-main/30 border-t-primary-main"
          />
          <DMText as="span">
            Publishing post
            {publishingPlatform
              ? ` on ${postPlatformLabels[publishingPlatform]}`
              : ''}
            . Please wait...
          </DMText>
        </DMDiv>
      )}
    </DMDiv>
  </Modal>
);

function CreatePostList() {
  const navigate = useNavigate();
  const location = useLocation();
  const authData = useAuth();
  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 900);
  const [isLoadingPosts, setIsLoadingPosts] = React.useState(true);
  const [postRows, setPostRows] = React.useState<PostItem[]>(() => cachedPostRows);
  const [totalPosts, setTotalPosts] = React.useState(() => cachedPostTotal);
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [postToDelete, setPostToDelete] = React.useState<PostItem | null>(null);
  const [postToPublish, setPostToPublish] = React.useState<PostItem | null>(
    null
  );
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isPublishingPost, setIsPublishingPost] = React.useState(false);
  const [publishingPlatform, setPublishingPlatform] =
    React.useState<PostPlatformKey | null>(null);
  const [platforms, setPlatforms] = React.useState(emptyPlatforms);
  const [postFilter, setPostFilter] = React.useState<'all' | 'live' | 'ready'>('all');

  const filteredPosts = React.useMemo(() => {
    const normalizedSearch = debouncedSearchQuery.trim().toLowerCase();
    return postRows.filter(item => {
      const isLive = isPostLive(item);
      const matchesFilter =
        postFilter === 'all' ||
        (postFilter === 'live' && isLive) ||
        (postFilter === 'ready' && !isLive);
      const matchesSearch =
        !normalizedSearch ||
        item.title.toLowerCase().includes(normalizedSearch) ||
        item.school.toLowerCase().includes(normalizedSearch) ||
        item.date.toLowerCase().includes(normalizedSearch) ||
        item.description.toLowerCase().includes(normalizedSearch);
      return matchesFilter && matchesSearch;
    });
  }, [debouncedSearchQuery, postFilter, postRows]);

  const fetchPosts = React.useCallback(async () => {
    setIsLoadingPosts(true);

    try {
      const response = await httpclient().get(
        `/master/post/list?page_index=${pageIndex}&page_size=${pageSize}&sort_by=POST_ID&sort_type=desc&_route=${encodeURIComponent(location.key)}`
      );
      const mappedPosts = getPostList(response).map(mapPostRow);
      const mappedTotal = getPostTotal(response);
      cachedPostRows = mappedPosts;
      cachedPostTotal = mappedTotal;
      setPostRows(mappedPosts);
      setTotalPosts(mappedTotal);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to fetch posts'),
        'error'
      );
      if (!cachedPostRows.length) {
        setPostRows([]);
        setTotalPosts(0);
      }
    } finally {
      setIsLoadingPosts(false);
    }
  }, [location.key, pageIndex, pageSize]);

  React.useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  React.useEffect(() => {
    setPageIndex(1);
  }, [debouncedSearchQuery, postFilter]);

  const handleConfirmDelete = async () => {
    if (!postToDelete) {
      return;
    }

    setIsDeleting(true);

    try {
      await httpclient().delete(
        `/master/post/delete?post_id=${postToDelete.id}`
      );
      showGlobalModal('Post deleted successfully');
      setTotalPosts(current => Math.max(0, current - 1));
      if (postRows.length === 1 && pageIndex > 1) {
        setPageIndex(current => current - 1);
      } else {
        fetchPosts();
      }
      setPostToDelete(null);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to delete post'),
        'error'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenPublishModal = (post: PostItem) => {
    setPlatforms({
      ...emptyPlatforms,
      facebook: post.facebook,
      instagram: post.instagram,
      youtube: post.youtube,
    });
    setPostToPublish(post);
  };

  const handleClosePublishModal = () => {
    if (isPublishingPost) {
      return;
    }

    setPostToPublish(null);
    setPlatforms(emptyPlatforms);
  };

  const publishPostPlatform = async (platformKey: PostPlatformKey) => {
    if (!postToPublish?.id) {
      showGlobalModal('Post id is missing', 'error');
      return;
    }

    if (!postToPublish.availablePlatforms.includes(platformKey)) {
      showGlobalModal(
        'This social platform is not available for this post',
        'warning'
      );
      return;
    }

    setIsPublishingPost(true);
    setPublishingPlatform(platformKey);

    let fallbackErrorMessage = 'Unable to update post platform';

    try {
      const platform = platformKey.toUpperCase();

      if (
        platformKey === 'facebook' ||
        platformKey === 'instagram' ||
        platformKey === 'youtube'
      ) {
        const platformLabel = postPlatformLabels[platformKey];

        if (platformKey === 'youtube' && !authData?.BRANCH_ID) {
          showGlobalModal('Branch id is missing', 'error');
          return;
        }

        const socialPostPayload = {
          ...buildFacebookPostPayload(postToPublish),
          ...(platformKey === 'youtube'
            ? { BRANCH_ID: authData?.BRANCH_ID as number | string }
            : {}),
        };

        if (!socialPostPayload.MEDIA.length) {
          showGlobalModal('Post media is missing', 'error');
          return;
        }

        fallbackErrorMessage = `Unable to publish post on ${platformLabel}`;
        const socialPostResponse = await httpclient().post(
          `/comm/${platformKey}/post`,
          socialPostPayload
        );
        openSocialPostUrl(socialPostResponse);
        fallbackErrorMessage = 'Unable to update post platform';
      }

      const response = await httpclient().post(
        `/master/post/updatePlatform/${postToPublish.id}`,
        { platform }
      );
      const updatedPost = response?.data?.data || {};

      setPostRows(current =>
        current.map(item =>
          item.id === postToPublish.id
            ? {
                ...item,
                facebook: Boolean(
                  updatedPost.facebook ??
                    (platformKey === 'facebook' ? true : item.facebook)
                ),
                instagram: Boolean(
                  updatedPost.instagram ??
                    (platformKey === 'instagram' ? true : item.instagram)
                ),
                youtube: Boolean(
                  updatedPost.youtube ??
                    (platformKey === 'youtube' ? true : item.youtube)
                ),
              }
            : item
        )
      );

      setPostToPublish(current =>
        current
          ? {
              ...current,
              facebook: Boolean(
                updatedPost.facebook ??
                  (platformKey === 'facebook' ? true : current.facebook)
              ),
              instagram: Boolean(
                updatedPost.instagram ??
                  (platformKey === 'instagram' ? true : current.instagram)
              ),
              youtube: Boolean(
                updatedPost.youtube ??
                  (platformKey === 'youtube' ? true : current.youtube)
              ),
            }
          : current
      );
      setPlatforms(current => ({ ...current, [platformKey]: true }));
      showGlobalModal(
        `Post published on ${postPlatformLabels[platformKey]} successfully`
      );
    } catch (error: any) {
      showGlobalModal(getApiErrorMessage(error, fallbackErrorMessage), 'error');
    } finally {
      setIsPublishingPost(false);
      setPublishingPlatform(null);
    }
  };

  const requestPublishPlatform = (platformKey: PostPlatformKey) => {
    if (platforms[platformKey]) {
      return;
    }

    const platformLabel = postPlatformLabels[platformKey];
    showGlobalModal(
      `Are you sure you want to publish this post on ${platformLabel}? Once published, the post cannot be edited or deleted.`,
      'warning',
      {
        showConfirm: true,
        onConfirm: () => publishPostPlatform(platformKey),
        onCancel: () => undefined,
        confimLabel: 'Publish',
        cancelLabel: 'Cancel',
      }
    );
  };

  const totalPostCount = postRows.filter(() => true).length;
  const livePostCount = postRows.filter(
    isPostLive
  ).length;
  const readyPostCount = postRows.filter(
    post => !isPostLive(post)
  ).length;
  const countsAreLoading = isLoadingPosts && !postRows.length;

  return (
    <DMDiv
      as="main"
      className="create-post cp-list-page"
    >
      <DigitalMarketingBreadcrumb
        className="mb-[2.2vh] shrink-0"
        items={[{ label: 'Posted Content' }]}
      />

      <DMDiv className="cp-list-head mb-[2vh] flex shrink-0 items-start justify-between gap-[2vw]">
        <DMDiv>
          <DMText
            as="h1"
            className="text-[3.5vh] font-bold leading-tight text-gray-950"
          >
            Posts
          </DMText>
          <DMText className="mt-[0.65vh] text-[1.65vh] font-medium leading-tight text-gray-600">
            Manage, publish, or refresh content created in the past.
          </DMText>
        </DMDiv>

        <DMDiv className="flex shrink-0 items-center gap-[0.8vw]">
          <DigitalMarketingSearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search posts"
            className="cp-post-search"
          />
          <DMButton
            type="button"
            onClick={() => navigate(buildCreatePostPath('create'))}
            className="cp-primary-button"
          >
            <Plus className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.1} />
            <DMText as="span">Create Post</DMText>
          </DMButton>
        </DMDiv>
      </DMDiv>

      <DMDiv className="cp-stats-strip">
        <DMButton type="button" className={`cp-stat-card${postFilter === 'all' ? ' active' : ''}`} aria-pressed={postFilter === 'all'} onClick={() => setPostFilter('all')}><DMText as="span"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="3" /><path d="M3 9h18" /></svg></DMText><DMDiv><DMText as="span" className="cp-stat-label">TOTAL POSTS</DMText><DMText as="strong">{countsAreLoading ? '—' : totalPostCount}</DMText></DMDiv></DMButton>
        <DMButton type="button" className={`cp-stat-card${postFilter === 'live' ? ' active' : ''}`} aria-pressed={postFilter === 'live'} onClick={() => setPostFilter('live')}><DMText as="span"><ReferenceStrokeIcon path="M22 12h-4l-3 8-4-16-3 8H2" /></DMText><DMDiv><DMText as="span" className="cp-stat-label">LIVE ON SOCIAL</DMText><DMText as="strong">{countsAreLoading ? '—' : livePostCount} published</DMText></DMDiv></DMButton>
        <DMButton type="button" className={`cp-stat-card${postFilter === 'ready' ? ' active' : ''}`} aria-pressed={postFilter === 'ready'} onClick={() => setPostFilter('ready')}><DMText as="span"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg></DMText><DMDiv><DMText as="span" className="cp-stat-label">READY TO PUBLISH</DMText><DMText as="strong">{countsAreLoading ? '—' : readyPostCount} waiting</DMText></DMDiv></DMButton>
      </DMDiv>

      <PaginatedCardGrid
        className="cp-list-grid-card"
        emptyMessage="No posts found."
        hasItems={filteredPosts.length > 0}
        isLoading={isLoadingPosts}
        itemLabel="post"
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 30, 40, 50]}
        gridClassName="cp-post-grid"
        totalItems={totalPosts}
        onPageIndexChange={setPageIndex}
        onPageSizeChange={setPageSize}
        renderSkeleton={index => <PostCardSkeleton key={index} />}
      >
        {filteredPosts.map(item => (
          <PostCard
            key={`${item.id}-${item.title}`}
            item={item}
            onPreview={post =>
              navigate(buildCreatePostPath(`preview/${post.id}`), {
                state: { postItem: post },
              })
            }
            onEdit={post =>
              navigate(buildCreatePostPath(`edit/${post.id}`), {
                state: { postItem: post },
              })
            }
            onDelete={setPostToDelete}
            onPublish={handleOpenPublishModal}
          />
        ))}
      </PaginatedCardGrid>
      <DigitalMarketingDeleteModal
        isOpen={!!postToDelete}
        title="Are you sure you want to delete this post?"
        description="This action cannot be undone, and the post will be removed."
        isDeleting={isDeleting}
        onCancel={() => setPostToDelete(null)}
        onConfirm={handleConfirmDelete}
      />
      <PostPublishModal
        isOpen={!!postToPublish}
        onClose={handleClosePublishModal}
        platforms={platforms}
        requestPublish={requestPublishPlatform}
        isPublishing={isPublishingPost}
        publishingPlatform={publishingPlatform}
        visiblePlatforms={postToPublish?.availablePlatforms || []}
      />
    </DMDiv>
  );
}

function CreatePostForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const authData = useAuth();
  const postId = Number(params.postId || 0);
  const isEdit = Boolean(postId);
  const [postForm, setPostForm] = React.useState<PostFormValues>(() => {
    const postItem: PostItem | undefined = location.state?.postItem;

    if (!postItem) {
      return emptyPostForm;
    }

    const copy = splitPostCopy(postItem.description);

    return {
      postId: postItem.id,
      title: postItem.title,
      description: copy.description,
      hashtags: copy.hashtags,
      images: postItem.images.map((url, index) => ({
        url,
        name: `Image ${index + 1}`,
      })),
    };
  });
  const [isUploadingImage, setIsUploadingImage] = React.useState(false);
  const [enhancingImageUrl, setEnhancingImageUrl] = React.useState('');
  const { enhanceImage, isEnhancingImage } = useImageEnhancer();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isLoadingPostDetails, setIsLoadingPostDetails] =
    React.useState(isEdit);
  const [isGeneratingDescription, setIsGeneratingDescription] =
    React.useState(false);
  const [descriptionGenerationCount, setDescriptionGenerationCount] =
    React.useState(0);
  const [previewPlatform, setPreviewPlatform] =
    React.useState<PreviewPlatformKey>('facebook');
  React.useEffect(() => {
    if (postForm.images.length > 1 && previewPlatform === 'gmb') {
      setPreviewPlatform('facebook');
    }
  }, [postForm.images.length, previewPlatform]);
  const descriptionGenerationsRemaining =
    maxDescriptionGenerations - descriptionGenerationCount;
  const descriptionGenerateButtonLabel =
    postForm.description.trim() || descriptionGenerationCount > 0
      ? 'Re-generate'
      : 'Generate';

  React.useEffect(() => {
    if (!isEdit) {
      return;
    }

    const fetchPostDetails = async () => {
      setIsLoadingPostDetails(true);

      try {
        const response = await httpclient().get(
          `/master/post/view?post_id=${postId}`
        );
        const details = mapPostRow(getPostDetails(response));
        const copy = splitPostCopy(details.description);
        setPostForm({
          postId: details.id,
          title: details.title,
          description: copy.description,
          hashtags: copy.hashtags,
          images: details.images.map((url, index) => ({
            url,
            name: `Image ${index + 1}`,
          })),
        });
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(error, 'Unable to fetch post details'),
          'error'
        );
        navigate(CREATE_POST_BASE_PATH);
      } finally {
        setIsLoadingPostDetails(false);
      }
    };

    fetchPostDetails();
  }, [isEdit, navigate, postId]);

  const handleUploadImages = async (files: FileList | null) => {
    const selectedFiles = Array.from(files || []);

    if (!selectedFiles.length) {
      return;
    }

    const invalidFile = selectedFiles.find(
      file => !allowedImageTypes.includes(file.type)
    );
    if (invalidFile) {
      showGlobalModal(
        'Only PNG, JPG, and JPEG image files are allowed',
        'error'
      );
      return;
    }

    const oversizedFile = selectedFiles.find(file => file.size > maxImageSize);
    if (oversizedFile) {
      showGlobalModal('Each image must be 15MB or less', 'error');
      return;
    }

    setIsUploadingImage(true);

    try {
      const uploadedImages: PostImage[] = [];

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('dest', 'DM-CONTENT');
        formData.append('branch_id', authData?.BRANCH_ID as any);
        formData.append('database', authData?.DATABASE as any);
        formData.append('files', file);

        const response = await apiFileUpload(
          formData,
          'upload',
          '',
          '',
          true,
          false,
          true
        );
        const imageUrl = extractUploadedUrl(response);

        if (!imageUrl) {
          showGlobalModal(
            `Unable to read uploaded image URL for ${file.name}`,
            'error'
          );
          continue;
        }

        uploadedImages.push({ url: imageUrl, name: file.name });
      }

      if (uploadedImages.length) {
        setPostForm(current => ({
          ...current,
          images: [...current.images, ...uploadedImages],
        }));
      }
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Failed to upload image'),
        'error'
      );
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = (imageUrl: string) => {
    setPostForm(current => ({
      ...current,
      images: current.images.filter(image => image.url !== imageUrl),
    }));
  };

  const resetPostForm = () => {
    setPostForm(current => ({ ...emptyPostForm, postId: current.postId }));
    setDescriptionGenerationCount(0);
  };

  const readinessSteps = [
    { label: 'Title', done: Boolean(postForm.title.trim()) },
    { label: 'Description', done: Boolean(postForm.description.trim()) },
    { label: 'Hashtags', done: Boolean(postForm.hashtags.trim()) },
    { label: 'Images', done: postForm.images.length > 0 },
  ];
  const readiness = Math.round(
    (readinessSteps.filter(step => step.done).length / readinessSteps.length) *
      100
  );
  const hashtagCount = (postForm.hashtags.match(/#[\w-]+/g) || []).length;

  const handleEnhanceImage = async (image: PostImage) => {
    setEnhancingImageUrl(image.url);
    const enhancedUrl = await enhanceImage(image.url);

    if (enhancedUrl) {
      setPostForm(current => ({
        ...current,
        images: current.images.map(currentImage =>
          currentImage.url === image.url
            ? { ...currentImage, url: enhancedUrl }
            : currentImage
        ),
      }));
    }

    setEnhancingImageUrl('');
  };

  const handleGenerateDescription = async () => {
    if (descriptionGenerationsRemaining <= 0) {
      showGlobalModal('Description generation limit reached.', 'warning');
      return;
    }

    if (!postForm.title.trim()) {
      showGlobalModal(
        'Complete title before generating a description.',
        'warning'
      );
      return;
    }

    setIsGeneratingDescription(true);

    try {
      const description = await dmTextGenerationApi.generateDescription({
        type: 'CONTENT',
        title: postForm.title,
        category_type: 'POST',
      });

      if (description) {
        const generatedCopy = splitPostCopy(description);
        setPostForm(current => ({
          ...current,
          description: generatedCopy.description || description,
          hashtags: generatedCopy.hashtags || current.hashtags,
        }));
        setDescriptionGenerationCount(current =>
          Math.min(current + 1, maxDescriptionGenerations)
        );
      } else {
        showGlobalModal('Unable to generate description', 'error');
      }
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to generate description'),
        'error'
      );
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleCreatePost = async () => {
    if (!postForm.title || !postForm.description || !postForm.images.length) {
      showGlobalModal(
        'Complete required post details before uploading',
        'warning'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const imageUrls = postForm.images.map(image => image.url);
      const response = await httpclient().post('/master/upsert/post', {
        post_id: postForm.postId || postId || 0,
        title: postForm.title,
        description: combinePostCopy(
          postForm.description,
          postForm.hashtags
        ),
        image_urls: imageUrls,
      });

      showGlobalModal(
        response?.data?.message ||
          (isEdit ? 'Post updated successfully' : 'Post created successfully')
      );
      const savedPostId = Number(
        response?.data?.data?.post_id ||
          response?.data?.data?.POST_ID ||
          response?.data?.data?.id ||
          postForm.postId ||
          postId ||
          0
      );

      navigate(
        savedPostId
          ? buildCreatePostPath(`preview/${savedPostId}`)
          : CREATE_POST_BASE_PATH
      );
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(
          error,
          isEdit ? 'Unable to update post' : 'Unable to create post'
        ),
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingPostDetails) {
    return (
      <DMDiv
        as="main"
        className="flex h-full min-h-0 items-center justify-center bg-white"
      >
        <SetupLoader />
      </DMDiv>
    );
  }

  return (
    <DMDiv as="main" className="create-post cp-form-page">
      <DMDiv className="cp-page-head">
        <DMDiv className="cp-heading-group">
          <DMButton
            type="button"
            className="cp-icon-button"
            onClick={() => navigate(CREATE_POST_BASE_PATH)}
            aria-label="Back to posted content"
          >
            <ReferenceStrokeIcon path="M19 12H5M11 18l-6-6 6-6" />
          </DMButton>
          <DMDiv>
            <DMText as="h1">{isEdit ? 'Edit Post' : 'Create Post'}</DMText>
            <DMText>
              {isEdit
                ? 'Refresh the content or images, then update your post.'
                : 'Create engaging content for your school community.'}
            </DMText>
          </DMDiv>
        </DMDiv>
        <DMButton
          type="button"
          className="cp-secondary-button"
          onClick={() => navigate(CREATE_POST_BASE_PATH)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2.6" /><path d="m4 18 5-4.5 3.5 3L16 13l4 4" /></svg> View Past Posts
        </DMButton>
      </DMDiv>

      <DMDiv className="cp-create-layout">
        <DMDiv as="form" className="cp-editor-card">
          <DMDiv className="cp-readiness-steps" aria-label="Post readiness">
            {readinessSteps.map(step => (
              <DMDiv className={step.done ? 'done' : ''} key={step.label}>
                <DMText as="span"><Check /></DMText>
                <DMText as="strong">{step.label}</DMText>
              </DMDiv>
            ))}
          </DMDiv>

          <DMDiv className="cp-editor-scroll">
            <DMDiv className="cp-field-block">
              <DMDiv className="cp-field-heading">
                <DMText as="span" className="cp-field-icon">
                  <ReferenceStrokeIcon path="M4 7h16M4 12h10M4 17h7" />
                </DMText>
                <DMLabel htmlFor="post-title">Title <b>*</b></DMLabel>
                <DMText as="span">— what parents see first</DMText>
                <DMText as="span" className="cp-counter">{postForm.title.length}/80</DMText>
              </DMDiv>
              <DMInput
                id="post-title"
                maxLength={80}
                placeholder="e.g. Annual Day Highlights 2026"
                value={postForm.title}
                onChange={event => setPostForm(current => ({
                  ...current,
                  title: event.target.value,
                }))}
              />
            </DMDiv>

            <DMDiv className="cp-ai-heading">
              <DMText as="h2">AI description &amp; hashtags</DMText>
              <DMText>
                {postForm.title.trim()
                  ? `Drafted from “${postForm.title.trim()}” — edit until it sounds right.`
                  : 'Add a title above to generate a description and hashtags.'}
              </DMText>
            </DMDiv>

            <DMDiv className="cp-field-block cp-copy-field">
              <DMDiv className="cp-field-heading">
                <DMLabel htmlFor="post-description">Description <b>*</b></DMLabel>
                <DMButton
                  type="button"
                  className="cp-ai-pill"
                  onClick={handleGenerateDescription}
                  disabled={
                    !postForm.title.trim() ||
                    isGeneratingDescription ||
                    descriptionGenerationsRemaining <= 0
                  }
                >
                  <Sparkles />
                  {isGeneratingDescription
                    ? 'Writing…'
                    : `${descriptionGenerateButtonLabel} with AI`}
                </DMButton>
              </DMDiv>
              <DMDiv className="cp-textarea-wrap">
                <DMTextarea
                  id="post-description"
                  placeholder="Enter a description, or let AI draft one for you"
                  value={postForm.description}
                  onChange={event => setPostForm(current => ({
                    ...current,
                    description: event.target.value,
                  }))}
                />
                {isGeneratingDescription && (
                  <DMDiv className="cp-ai-loading" role="status">
                    <DMText as="span"><Sparkles /></DMText>
                    <DMDiv>
                      <DMText as="strong">Drafting content parents will love…</DMText>
                      <DMText>Reading your title and polishing the tone.</DMText>
                    </DMDiv>
                  </DMDiv>
                )}
              </DMDiv>
              <DMText as="span" className="cp-retry-copy">
                {descriptionGenerationsRemaining} retries remaining
              </DMText>
            </DMDiv>

            <DMDiv className="cp-field-block cp-copy-field">
              <DMDiv className="cp-field-heading">
                <DMLabel htmlFor="post-hashtags">Hashtags</DMLabel>
                {hashtagCount > 0 && (
                  <DMText as="span" className="cp-success-pill">
                    {hashtagCount} TAGS · READY
                  </DMText>
                )}
              </DMDiv>
              <DMDiv className="cp-hashtag-input">
                <Hash />
                <DMTextarea
                  id="post-hashtags"
                  placeholder="schoollife annualday learning"
                  value={postForm.hashtags.replace(/^#/, '')}
                  onChange={event => setPostForm(current => ({
                    ...current,
                    hashtags: event.target.value
                      .split(/\s+/)
                      .filter(Boolean)
                      .map(tag => `#${tag.replace(/^#+/, '')}`)
                      .join(' '),
                  }))}
                />
              </DMDiv>
            </DMDiv>

            <DMDiv className="cp-field-block cp-image-section">
              <DMDiv className="cp-field-heading">
                <DMText as="span" className="cp-field-icon warm">
                  <ReferenceStrokeIcon path="M3 5.5h18v13H3zM4 17l5-4.5 3.5 3L16 12l4 4" />
                </DMText>
                <DMLabel>Upload Images <b>*</b></DMLabel>
                <DMText as="span">— posts with images get 3× more engagement</DMText>
                {postForm.images.length > 0 && (
                  <DMText as="span" className="cp-success-pill cp-counter">
                    {postForm.images.length} SELECTED
                  </DMText>
                )}
              </DMDiv>
              <DMDiv className="cp-quality-note"><Sparkles /> Low quality images? Upload your own — we deliver them in HD.</DMDiv>

              <DMDiv
                className={`cp-dropzone ${postForm.images.length ? 'has-images' : ''}`}
                onDragOver={event => event.preventDefault()}
                onDrop={event => {
                  event.preventDefault();
                  handleUploadImages(event.dataTransfer.files);
                }}
              >
                {postForm.images.length ? (
                  <DMDiv className="cp-image-grid">
                    {postForm.images.map(image => (
                      <DMDiv className="cp-image-tile" key={image.url}>
                        <DMImage src={image.url} alt={image.name} />
                        <DMText as="span" className="cp-ai-checked"><Sparkles /> AI CHECKED</DMText>
                        <DMButton
                          type="button"
                          className="cp-image-enhance"
                          onClick={() => handleEnhanceImage(image)}
                          disabled={isUploadingImage || isEnhancingImage}
                          aria-label={`Enhance ${image.name}`}
                        ><Sparkles /></DMButton>
                        <DMButton
                          type="button"
                          className="cp-image-remove"
                          onClick={() => handleRemoveImage(image.url)}
                          disabled={isEnhancingImage}
                          aria-label={`Remove ${image.name}`}
                        ><X /></DMButton>
                        <DMText as="span" className="cp-image-name">{image.name}</DMText>
                        {enhancingImageUrl === image.url && <DMText className="cp-enhancing">Enhancing…</DMText>}
                      </DMDiv>
                    ))}
                    <DMLabel className="cp-add-image">
                      <Plus /> <DMText as="span">Add more</DMText>
                      <DMInput
                        type="file"
                        accept="image/png,image/jpg,image/jpeg"
                        multiple
                        disabled={isUploadingImage || isEnhancingImage}
                        onChange={event => {
                          handleUploadImages(event.target.files);
                          event.currentTarget.value = '';
                        }}
                      />
                    </DMLabel>
                  </DMDiv>
                ) : (
                  <>
                    <DMDiv className="cp-upload-art" aria-hidden="true">
                      <i />
                      <DMText as="span"><ReferenceStrokeIcon path="M12 5v14M5 12h14" /></DMText>
                    </DMDiv>
                    <DMDiv>
                      <DMText as="strong">Drag &amp; drop your images here</DMText>
                      <DMText>PNG, JPG, JPEG · max 15mb each · up to 10 images</DMText>
                    </DMDiv>
                    <DMLabel className="cp-browse-button">
                      <ReferenceStrokeIcon path="M12 16V4M7.5 8.5 12 4l4.5 4.5M4 17v1.6A2.4 2.4 0 0 0 6.4 21h11.2A2.4 2.4 0 0 0 20 18.6V17" /> Browse files
                      <DMInput
                        type="file"
                        accept="image/png,image/jpg,image/jpeg"
                        multiple
                        disabled={isUploadingImage || isEnhancingImage}
                        onChange={event => {
                          handleUploadImages(event.target.files);
                          event.currentTarget.value = '';
                        }}
                      />
                    </DMLabel>
                  </>
                )}
              </DMDiv>
              {isUploadingImage && <DMDiv className="cp-uploading" role="status"><UploadCloud /> Uploading &amp; optimising your images…</DMDiv>}
            </DMDiv>
          </DMDiv>

          <DMDiv className="cp-form-footer">
            <DMText><DMText as="span">{readiness}%</DMText> post ready</DMText>
            <DMDiv>
              <DMButton type="button" className="cp-secondary-button" onClick={resetPostForm}>
                <RefreshCw /> Refresh
              </DMButton>
              <DMButton
                type="button"
                className="cp-primary-button"
                onClick={handleCreatePost}
                disabled={
                  !postForm.title.trim() ||
                  !postForm.description.trim() ||
                  !postForm.images.length ||
                  isUploadingImage ||
                  isEnhancingImage ||
                  isSubmitting
                }
              >
                {isSubmitting ? <SetupLoader height="h-5" /> : <><Check /> {isEdit ? 'Update Post' : 'Create Post'}</>}
              </DMButton>
            </DMDiv>
          </DMDiv>
        </DMDiv>

        <DMDiv as="aside" className="cp-preview-card">
          <DMDiv className="cp-preview-head">
            <DMText as="span"><i /> LIVE PREVIEW</DMText>
            <DMText as="span" className="cp-preview-platform-name">{previewPlatformMeta[previewPlatform].name} preview</DMText>
          </DMDiv>
          <DMDiv className="cp-readiness-bar">
            <DMText as="span">POST READINESS</DMText>
            <i><b style={{ width: `${readiness}%` }} /></i>
            <DMText as="strong">{readiness}%</DMText>
          </DMDiv>
          <DMDiv className="cp-platform-tabs">
            {(Object.keys(previewPlatformMeta) as PreviewPlatformKey[])
              .filter(platform => platform !== 'gmb' || postForm.images.length <= 1)
              .map(platform => (
              <DMButton
                type="button"
                key={platform}
                className={previewPlatform === platform ? 'active' : ''}
                onClick={() => setPreviewPlatform(platform)}
                aria-label={`Preview on ${previewPlatformMeta[platform].name}`}
              >
                <ReferencePathIcon path={previewPlatformMeta[platform].path} />
              </DMButton>
            ))}
          </DMDiv>
          <DMDiv className="cp-social-preview">
            <DMDiv className="cp-social-head">
              <DMText
                as="span"
                style={{ background: `linear-gradient(140deg,${previewPlatformMeta[previewPlatform].accent},#4F46E5)` }}
              >S</DMText>
              <DMDiv>
                <DMText as="strong">{previewPlatformMeta[previewPlatform].handle}</DMText>
                <DMText as="span" className="cp-social-time">
                  Just now · <b style={{ color: previewPlatformMeta[previewPlatform].accent }}>{previewPlatformMeta[previewPlatform].audience}</b>
                </DMText>
              </DMDiv>
              <DMText
                as="span"
                className="cp-social-platform-label"
                style={{ background: previewPlatformMeta[previewPlatform].accent }}
              >
                <ReferencePathIcon path={previewPlatformMeta[previewPlatform].path} />
                {previewPlatformMeta[previewPlatform].name}
              </DMText>
            </DMDiv>
            <DMDiv
              className="cp-preview-media"
              style={{ aspectRatio: previewPlatformMeta[previewPlatform].aspect }}
            >
              <DMText as="span" className="cp-preview-ratio">{previewPlatformMeta[previewPlatform].ratio} fit</DMText>
              {postForm.images[0] ? (
                <DMImage src={postForm.images[0].url} alt="Post preview" />
              ) : (
                <DMDiv>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="16" rx="2.6" />
                    <circle cx="9" cy="10" r="1.8" />
                    <path d="m4 18 5-4.5 3.5 3L16 13l4 4" />
                  </svg>
                  <DMText>Your image appears here</DMText>
                </DMDiv>
              )}
            </DMDiv>
            <DMDiv className="cp-preview-copy">
              <DMText as="strong">{postForm.title || 'Your post title will appear here'}</DMText>
              <DMText className="cp-preview-tags">{postForm.hashtags || '#hashtags #willAppearHere'}</DMText>
              <DMDiv>
                {previewPlatformMeta[previewPlatform].actions.map(action => (
                  <DMText as="span" key={action.label}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" aria-hidden="true"><path d={action.path} /></svg>
                    {action.label}
                  </DMText>
                ))}
              </DMDiv>
            </DMDiv>
          </DMDiv>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
}

function CreatePostPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const authData = useAuth();
  const postId = Number(params.postId || 0);
  const [postItem, setPostItem] = React.useState<PostItem | null>(
    location.state?.postItem || null
  );
  const [isLoading, setIsLoading] = React.useState(Boolean(postId));
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [isPublishing, setIsPublishing] = React.useState(false);
  const [publishingPlatform, setPublishingPlatform] =
    React.useState<PostPlatformKey | null>(null);
  const [platforms, setPlatforms] = React.useState(emptyPlatforms);
  const [selectedDetailPlatforms, setSelectedDetailPlatforms] = React.useState<DetailPlatformKey[]>([]);
  const [completedDetailPlatforms, setCompletedDetailPlatforms] = React.useState<DetailPlatformKey[]>([]);
  const [scheduledDetailPlatforms, setScheduledDetailPlatforms] = React.useState<DetailPlatformKey[]>([]);
  const [scheduledWhen, setScheduledWhen] = React.useState('');
  const [shareStatuses, setShareStatuses] = React.useState<Partial<Record<DetailPlatformKey, 'uploading' | 'done'>>>({});
  const [isSharingSelected, setIsSharingSelected] = React.useState(false);
  const [scheduleOpen, setScheduleOpen] = React.useState(false);
  const [scheduleDate, setScheduleDate] = React.useState('');
  const [scheduleTime, setScheduleTime] = React.useState('');
  const [scheduleError, setScheduleError] = React.useState('');
  const scheduleDateInputRef = React.useRef<HTMLInputElement>(null);
  const [whatsappContacts, setWhatsappContacts] = React.useState<Record<string, boolean>>({
    'All Parents': true,
    'Class 5A Parents': false,
    'Class 6B Parents': false,
    'Class 7A Parents': false,
  });

  React.useEffect(() => {
    if (!postId) {
      setIsLoading(false);
      return;
    }

    const fetchPostDetails = async () => {
      setIsLoading(true);

      try {
        const response = await httpclient().get(
          `/master/post/view?post_id=${postId}`
        );
        setPostItem(mapPostRow(getPostDetails(response)));
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(error, 'Unable to fetch post details'),
          'error'
        );
        navigate(CREATE_POST_BASE_PATH);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPostDetails();
  }, [navigate, postId]);

  React.useEffect(() => {
    if (!postItem) {
      return;
    }
    setPlatforms(current => ({
      ...current,
      facebook: postItem.facebook,
      instagram: postItem.instagram,
      youtube: postItem.youtube,
    }));
  }, [postItem]);

  if (isLoading) {
    return (
      <DMDiv
        as="main"
        className="flex h-full min-h-0 items-center justify-center bg-white"
      >
        <SetupLoader />
      </DMDiv>
    );
  }

  if (!postItem) {
    return null;
  }

  const previewImages = postItem.images.length
    ? postItem.images
    : [noImagesPlaceholder];
  const hasPublishedPlatform =
    postItem.facebook || postItem.instagram || postItem.youtube;
  const visiblePlatforms: PostPlatformKey[] = postItem.availablePlatforms.length
    ? postItem.availablePlatforms
    : ['facebook', 'instagram', 'youtube'];
  const sharedDetailPlatforms = detailPlatformMeta.filter(
    platform =>
      completedDetailPlatforms.includes(platform.key) ||
      (platform.backendKey && platforms[platform.backendKey])
  );
  const scheduledPlatformMeta = detailPlatformMeta.filter(platform =>
    scheduledDetailPlatforms.includes(platform.key) &&
    !sharedDetailPlatforms.some(shared => shared.key === platform.key)
  );
  const shareableDetailPlatforms = detailPlatformMeta.filter(
    platform =>
      !sharedDetailPlatforms.some(shared => shared.key === platform.key) &&
      !scheduledDetailPlatforms.includes(platform.key)
  );
  const toggleDetailPlatform = (key: DetailPlatformKey) => {
    setSelectedDetailPlatforms(current =>
      current.includes(key)
        ? current.filter(platform => platform !== key)
        : [...current, key]
    );
  };
  const allShareableSelected = shareableDetailPlatforms.length > 0 &&
    shareableDetailPlatforms.every(platform => selectedDetailPlatforms.includes(platform.key));
  const selectedPlatformMeta = detailPlatformMeta.filter(platform =>
    selectedDetailPlatforms.includes(platform.key)
  );
  const earliestScheduleAt = new Date(Date.now() + 60_000);
  const minScheduleDate = `${earliestScheduleAt.getFullYear()}-${String(earliestScheduleAt.getMonth() + 1).padStart(2, '0')}-${String(earliestScheduleAt.getDate()).padStart(2, '0')}`;
  const minScheduleTime = `${String(earliestScheduleAt.getHours()).padStart(2, '0')}:${String(earliestScheduleAt.getMinutes()).padStart(2, '0')}`;
  const selectedScheduleAt = scheduleDate && scheduleTime
    ? new Date(`${scheduleDate}T${scheduleTime}:00`)
    : null;
  const isScheduleInFuture = Boolean(
    selectedScheduleAt && !Number.isNaN(selectedScheduleAt.getTime()) && selectedScheduleAt.getTime() > Date.now()
  );

  const handleConfirmSchedule = () => {
    const latestScheduleAt = scheduleDate && scheduleTime
      ? new Date(`${scheduleDate}T${scheduleTime}:00`)
      : null;
    if (
      !latestScheduleAt ||
      Number.isNaN(latestScheduleAt.getTime()) ||
      latestScheduleAt.getTime() <= Date.now()
    ) {
      setScheduleError('Please select a future date and time.');
      return;
    }
    if (!selectedDetailPlatforms.length) {
      return;
    }
    setScheduleError('');
    const date = new Date(`${scheduleDate}T00:00:00`);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const [hours, minutes] = scheduleTime.split(':').map(Number);
    const formattedTime = `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
    setScheduledDetailPlatforms(current => Array.from(new Set([
      ...current,
      ...selectedDetailPlatforms,
    ])));
    setScheduledWhen(`${formattedDate} at ${formattedTime}`);
    setSelectedDetailPlatforms([]);
    setScheduleOpen(false);
    showGlobalModal(`Post scheduled on ${formattedDate}, at ${formattedTime}`);
  };

  const openScheduleDatePicker = () => {
    const dateInput = scheduleDateInputRef.current;
    if (!dateInput) {
      return;
    }
    const pickerInput = dateInput as HTMLInputElement & {
      showPicker?: () => void;
    };
    dateInput.focus();
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
    } else {
      dateInput.click();
    }
  };

  const handleShareSelected = async () => {
    if (!selectedDetailPlatforms.length || isSharingSelected) {
      return;
    }
    const selected = [...selectedDetailPlatforms];
    setIsSharingSelected(true);
    setShareStatuses(selected.reduce<Partial<Record<DetailPlatformKey, 'uploading' | 'done'>>>((statuses, key) => {
      statuses[key] = 'uploading';
      return statuses;
    }, {}));

    for (const key of selected) {
      await new Promise(resolve => window.setTimeout(resolve, 420));
      setShareStatuses(current => ({ ...current, [key]: 'done' }));
    }

    await new Promise(resolve => window.setTimeout(resolve, 260));
    setCompletedDetailPlatforms(current => Array.from(new Set([...current, ...selected])));
    setScheduledDetailPlatforms(current => current.filter(key => !selected.includes(key)));
    setSelectedDetailPlatforms([]);
    setShareStatuses({});
    setIsSharingSelected(false);
    showGlobalModal(`Post shared successfully to ${selected.length} platform${selected.length === 1 ? '' : 's'}`);
  };

  const requestPreviewPublish = (platformKey: PostPlatformKey) => {
    if (platforms[platformKey] || isPublishing) {
      return;
    }

    showGlobalModal(
      `Publish this post on ${postPlatformLabels[platformKey]}? Published posts can no longer be edited or deleted.`,
      'warning',
      {
        showConfirm: true,
        confimLabel: 'Publish',
        cancelLabel: 'Cancel',
        onCancel: () => undefined,
        onConfirm: async () => {
          setIsPublishing(true);
          setPublishingPlatform(platformKey);
          try {
            if (platformKey === 'youtube' && !authData?.BRANCH_ID) {
              showGlobalModal('Branch id is missing', 'error');
              return;
            }
            const payload = {
              ...buildFacebookPostPayload(postItem),
              ...(platformKey === 'youtube'
                ? { BRANCH_ID: authData?.BRANCH_ID as number | string }
                : {}),
            };
            const socialResponse = await httpclient().post(
              `/comm/${platformKey}/post`,
              payload
            );
            openSocialPostUrl(socialResponse);
            await httpclient().post(`/master/post/updatePlatform/${postItem.id}`, {
              platform: platformKey.toUpperCase(),
            });
            setPlatforms(current => ({ ...current, [platformKey]: true }));
            setPostItem(current =>
              current ? { ...current, [platformKey]: true } : current
            );
            showGlobalModal(
              `Post published on ${postPlatformLabels[platformKey]} successfully`
            );
          } catch (error: any) {
            showGlobalModal(
              getApiErrorMessage(
                error,
                `Unable to publish post on ${postPlatformLabels[platformKey]}`
              ),
              'error'
            );
          } finally {
            setIsPublishing(false);
            setPublishingPlatform(null);
          }
        },
      }
    );
  };

  const copy = splitPostCopy(postItem.description);
  const handleDownloadPost = () => {
    const link = document.createElement('a');
    link.href = previewImages[0];
    link.download = `${postItem.title || 'post'}.jpg`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  return (
    <DMDiv as="main" className="create-post cp-detail-page">
      <DMDiv className="cp-detail-page-head">
        <DMDiv className="cp-heading-group">
          <DMButton
            type="button"
            className="cp-icon-button"
            onClick={() => navigate(CREATE_POST_BASE_PATH)}
            aria-label="Back to posts"
          ><ReferenceStrokeIcon path="M19 12H5M11 18l-6-6 6-6" /></DMButton>
          <DMDiv>
            <DMDiv className="cp-detail-title-row">
              <DMText as="h1">View Post Details</DMText>
              <DMText as="span" className="cp-saved-chip"><Check /> SAVED TO LIBRARY</DMText>
            </DMDiv>
            <DMText>Review it, then pick the channels — you can share now or schedule for later.</DMText>
          </DMDiv>
        </DMDiv>
        <DMButton type="button" className="cp-secondary-button cp-download-button" onClick={handleDownloadPost}>
          <ReferenceStrokeIcon path="M12 4v12M7.5 11.5 12 16l4.5-4.5M4 18v1.6A2.4 2.4 0 0 0 6.4 22h11.2A2.4 2.4 0 0 0 20 19.6V18" /> Download Post
        </DMButton>
      </DMDiv>

      <DMDiv className="cp-detail-layout">
        <DMDiv className="cp-detail-media-rail">
          <DMDiv className="cp-detail-image-card">
            <DMImage
              src={previewImages[0]}
              alt={postItem.title}
              onError={event => { event.currentTarget.src = noImagesPlaceholder; }}
            />
            <DMDiv className="cp-detail-school-chip"><DMText as="span">S</DMText><DMText as="strong">Soomuch Demo School</DMText></DMDiv>
            <DMText as="span" className={`cp-detail-status ${hasPublishedPlatform ? 'live' : 'ready'}`}>
              {hasPublishedPlatform ? 'LIVE ON SOCIAL' : 'READY TO PUBLISH'}
            </DMText>
            {previewImages.length > 1 && (
              <DMText as="span" className="cp-detail-image-count">1 / {previewImages.length}</DMText>
            )}
            <DMDiv className="cp-detail-image-title"><DMText as="strong">{postItem.title}</DMText></DMDiv>
          </DMDiv>

          <DMDiv className="cp-detail-copy-card">
            <DMText as="span">DESCRIPTION</DMText>
            <DMText>{copy.description || 'No description added.'}</DMText>
            <DMText as="span">HASHTAGS</DMText>
            <DMText className="cp-preview-tags">{copy.hashtags || 'No hashtags added.'}</DMText>
          </DMDiv>
        </DMDiv>

        <DMDiv className="cp-platform-workspace">
          <DMDiv className="cp-platform-workspace-head">
            <DMText as="h2">Platforms</DMText>
            <DMText as="span">
              {sharedDetailPlatforms.length + scheduledPlatformMeta.length + selectedDetailPlatforms.length} of {detailPlatformMeta.length} platforms
            </DMText>
          </DMDiv>

          {sharedDetailPlatforms.length > 0 && (
            <>
              <DMDiv className="cp-platform-divider"><DMText as="span">SHARED</DMText><i /></DMDiv>
              <DMDiv className="cp-platform-grid">
                {sharedDetailPlatforms.map(platform => (
                  <DMDiv className="cp-platform-tile shared" key={`shared-${platform.key}`}>
                    <DMText as="span" className="cp-platform-check"><Check /></DMText>
                    <DMText as="span" className={`cp-platform-logo ${platform.chip}`}>
                      <DMText as="strong">{platform.code}</DMText>
                    </DMText>
                    <DMText as="strong">{platform.name}</DMText>
                    <DMText>Shared</DMText>
                    <DMText as="span" className="cp-platform-view">
                      View <ReferenceStrokeIcon path="M7 17 17 7M8 7h9v9" />
                    </DMText>
                  </DMDiv>
                ))}
              </DMDiv>
            </>
          )}

          {scheduledPlatformMeta.length > 0 && (
            <>
              <DMDiv className="cp-platform-divider"><DMText as="span">SCHEDULED</DMText><i /></DMDiv>
              <DMDiv className="cp-platform-grid">
                {scheduledPlatformMeta.map(platform => (
                  <DMDiv className="cp-platform-tile scheduled" key={`scheduled-${platform.key}`}>
                    <DMText as="span" className={`cp-platform-logo ${platform.chip}`}>
                      <DMText as="strong">{platform.code}</DMText>
                    </DMText>
                    <DMText as="strong">{platform.name}</DMText>
                    <DMText>Live {scheduledWhen}</DMText>
                  </DMDiv>
                ))}
              </DMDiv>
            </>
          )}

          {shareableDetailPlatforms.length > 0 && (
            <>
              <DMDiv className="cp-platform-divider cp-share-to-divider">
                <DMText as="span">SHARE TO</DMText><i />
                <DMButton
                  type="button"
                  className={allShareableSelected ? 'selected' : ''}
                  onClick={() => setSelectedDetailPlatforms(
                    allShareableSelected ? [] : shareableDetailPlatforms.map(platform => platform.key)
                  )}
                >
                  <DMText as="span"><Check /></DMText> Select All
                </DMButton>
              </DMDiv>
              <DMDiv className="cp-platform-grid">
                {shareableDetailPlatforms.map(platform => {
                  const selected = selectedDetailPlatforms.includes(platform.key);
                  const shareStatus = shareStatuses[platform.key];
                  return (
                  <DMButton
                    type="button"
                    className={`cp-platform-tile${selected ? ' selected' : ''}`}
                    key={platform.key}
                    disabled={isSharingSelected}
                    onClick={() => toggleDetailPlatform(platform.key)}
                  >
                    <DMText as="span" className={`cp-platform-radio${shareStatus === 'uploading' ? ' uploading' : ''}`}>
                      {shareStatus === 'uploading' ? <RefreshCw /> : selected && <Check />}
                    </DMText>
                    <DMText as="span" className={`cp-platform-logo ${platform.chip}`}>
                      <DMText as="strong">{platform.code}</DMText>
                    </DMText>
                    <DMText as="strong">{platform.name}</DMText>
                    <DMText>{platform.sub}</DMText>
                    {platform.key === 'whatsapp' && selected && (
                      <DMText as="span" className="cp-whatsapp-options" onClick={event => event.stopPropagation()}>
                        {Object.entries(whatsappContacts).map(([label, checked]) => (
                          <DMText
                            as="span"
                            key={label}
                            role="checkbox"
                            aria-checked={checked}
                            tabIndex={0}
                            className={checked ? 'selected' : ''}
                            onClick={() => setWhatsappContacts(current => ({
                              ...current,
                              [label]: !current[label],
                            }))}
                            onKeyDown={event => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setWhatsappContacts(current => ({
                                  ...current,
                                  [label]: !current[label],
                                }));
                              }
                            }}
                          >{label}</DMText>
                        ))}
                      </DMText>
                    )}
                    {shareStatus === 'uploading' && <DMText as="span" className="cp-platform-progress"><i /></DMText>}
                  </DMButton>
                  );
                })}
              </DMDiv>
            </>
          )}

          <DMDiv className="cp-platform-spacer" />
          <DMDiv className="cp-platform-info"><Info /> Each platform uploads independently — you'll see live progress per channel.</DMDiv>

          <DMDiv className="cp-detail-actions">
            <DMButton
              type="button"
              className="cp-secondary-button"
              disabled={!selectedDetailPlatforms.length}
              onClick={() => setScheduleOpen(true)}
            ><ReferenceStrokeIcon path="M5 4h14v16H5zM8 2v4M16 2v4M5 9h14M12 12v4l2.5 1.5" /> Schedule</DMButton>
            <DMButton
              type="button"
              className="cp-primary-button"
              disabled={!selectedDetailPlatforms.length || isSharingSelected}
              onClick={handleShareSelected}
            >
              <ReferenceStrokeIcon path="M12 16V3M8 7l4-4 4 4M5 12v7h14v-7" />
              {isSharingSelected ? 'Sharing…' : 'Share to Selected Platforms'}
            </DMButton>
          </DMDiv>
        </DMDiv>
      </DMDiv>

      {previewImages.length > 1 && (
        <DMDiv className="cp-detail-thumbs" aria-label="Post images">
          {previewImages.map((image, index) => (
            <DMImage key={`${image}-${index}`} src={image} alt={`${postItem.title} ${index + 1}`} />
          ))}
        </DMDiv>
      )}

      {scheduleOpen && (
        <DMDiv className="cp-schedule-overlay" role="dialog" aria-modal="true" aria-labelledby="schedule-post-title">
          <DMButton type="button" className="cp-schedule-backdrop" onClick={() => setScheduleOpen(false)} aria-label="Close schedule modal" />
          <DMDiv className="cp-schedule-modal">
            <DMDiv className="cp-schedule-head">
              <DMDiv>
                <DMText as="h2" id="schedule-post-title">Schedule Post</DMText>
                <DMText>Pick a date and time to schedule this post to your selected platforms.</DMText>
              </DMDiv>
              <DMButton type="button" onClick={() => setScheduleOpen(false)} aria-label="Close schedule modal">
                <ReferenceStrokeIcon path="M18 6 6 18M6 6l12 12" />
              </DMButton>
            </DMDiv>
            <DMDiv className="cp-schedule-fields">
              <DMLabel>
                <DMText as="span">SCHEDULED DATE</DMText>
                <DMDiv className="cp-schedule-input-wrap">
                  <DMInput
                    ref={scheduleDateInputRef}
                    type="date"
                    min={minScheduleDate}
                    value={scheduleDate}
                    onChange={event => {
                      const nextDate = event.target.value;
                      setScheduleDate(nextDate);
                      setScheduleError('');
                      if (nextDate === minScheduleDate && scheduleTime && scheduleTime < minScheduleTime) {
                        setScheduleTime('');
                      }
                    }}
                  />
                  <DMButton
                    type="button"
                    className="cp-schedule-field-icon"
                    onClick={openScheduleDatePicker}
                    aria-label="Open scheduled date calendar"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="5" width="18" height="16" rx="2.5" />
                      <path d="M8 3v4M16 3v4M3 10h18" />
                    </svg>
                  </DMButton>
                </DMDiv>
              </DMLabel>
              <DMLabel>
                <DMText as="span">SCHEDULED TIME</DMText>
                <DMInput
                  type="time"
                  min={scheduleDate === minScheduleDate ? minScheduleTime : undefined}
                  value={scheduleTime}
                  disabled={!scheduleDate}
                  onChange={event => {
                    setScheduleTime(event.target.value);
                    setScheduleError('');
                  }}
                />
              </DMLabel>
            </DMDiv>
            {(scheduleError || (scheduleDate && scheduleTime && !isScheduleInFuture)) && (
              <DMText className="cp-schedule-error" role="alert">
                {scheduleError || 'Please select a future date and time.'}
              </DMText>
            )}
            <DMDiv className="cp-schedule-summary">
              <Info />
              <DMText>Scheduling to the <DMText as="strong">{selectedDetailPlatforms.length}</DMText> platform(s) selected above.</DMText>
              <DMDiv>
                {selectedPlatformMeta.map(platform => (
                  <DMText as="span" className={`cp-platform-logo ${platform.chip}`} key={`schedule-chip-${platform.key}`}>{platform.code}</DMText>
                ))}
              </DMDiv>
            </DMDiv>
            <DMDiv className="cp-schedule-actions">
              <DMButton type="button" onClick={() => setScheduleOpen(false)}>Cancel</DMButton>
              <DMButton type="button" disabled={!isScheduleInFuture || !selectedDetailPlatforms.length} onClick={handleConfirmSchedule}>Confirm Schedule</DMButton>
            </DMDiv>
          </DMDiv>
        </DMDiv>
      )}

      <PostPublishModal
        isOpen={publishOpen}
        onClose={() => setPublishOpen(false)}
        platforms={platforms}
        visiblePlatforms={visiblePlatforms}
        requestPublish={requestPreviewPublish}
        isPublishing={isPublishing}
        publishingPlatform={publishingPlatform}
      />
    </DMDiv>
  );
}

export function CreatePost() {
  const location = useLocation();
  const isCreate = location.pathname.endsWith('/create');
  const isEdit = location.pathname.includes('/edit/');
  const isPreview = location.pathname.includes('/preview/');

  if (isCreate || isEdit) {
    return <CreatePostForm />;
  }

  if (isPreview) {
    return <CreatePostPreview />;
  }

  return <CreatePostList />;
}
